<template>
  <div
    class="d-flex align-items-center"
    :class="{ create, update, inactive: !active && !update && !create }"
  >
    <div
      v-if="create"
      class="checkbox"
    >
      <i class="fas fa-circle-plus" />
    </div>
    <div
      v-else-if="update"
      class="checkbox"
    >
      <i class="fas fa-edit" />
    </div>
    <div
      v-else-if="!hideCheckbox"
      class="checkbox"
      @click="$emit('update:checked', !checked)"
    >
      <Checkbox
        :value="checked"
        class="px-0"
      />
    </div>
    <div
      v-else
      class="checkbox"
    />
    <div
      class="listing-row"
      :class="{ selected }"
      @click="$emit('select')"
    >
      <div class="d-flex align-items-center row-content">
        <div class="font-weight-bold listing-name d-flex align-items-center">
          <div>
            <img
              v-if="listing.photoUrl"
              class="photo"
              :src="listing.photoUrl"
            >
          </div>
          <div>
            <span
              :class="{ modified: modifiedFields.includes('name') }"
              class="listing-name-text"
            >
              {{ listing.name }}
            </span>
            <div
              v-if="listing.importTimetable"
              class="small font-weight-normal text-secondary"
              :class="{ modified: modifiedFields.includes('timetable') }"
            >
              <i class="far fa-calendar pr-1" />
              {{ timetableText || $t('import.noTimetable') }}
            </div>
          </div>
        </div>
        <Tippy
          theme="light"
          delay="200"
        >
          <template #trigger>
            <div
              class="text-left d-flex flex-wrap align-items-center"
              :class="{ modified: modifiedFields.includes('productIds') }"
              style="min-width: 200px; max-width: 200px; max-height: 40px; overflow: hidden;"
            >
              <div
                v-for="a in listingProducts.slice(0, 3)"
                :key="a.id"
                class="badge product-badge px-2 mr-1"
              >
                {{ a.name }}
              </div>
              <div
                v-if="listingProducts.length > 3"
                class="badge product-badge px-2 mr-1"
              >
                +{{ listingProducts.length - 3 }}
              </div>
            </div>
          </template>

          <div
            v-for="a in listingProducts"
            :key="a.id"
            class="badge product-badge px-2 mr-1"
          >
            {{ a.name }}
          </div>
        </Tippy>
        <div
          v-if="!nameOnly"
          :class="{ modified: modifiedFields.includes('price') }"
          class="money-font text-right money-column"
        >
          {{ formatCurrency(listing.price, 'pln') }}
        </div>
        <div
          v-for="s in listingFields"
          :key="s.fieldId"
          v-tippy
          class="flex-grow-1 small listing-nodes"
          :class="{ modified: modifiedFields.includes(s.fieldId) }"
        >
          <span>
            {{ s.value }}
          </span>
        </div>
        <div
          v-for="s in subCatalogsSorted"
          :key="s.subCatalogId"
          v-tippy
          class="flex-grow-1 small listing-nodes"
          :content="s.nodeNames.join('<br>')"
          :class="{ modified: modifiedFields.includes(s.subCatalogId) }"
        >
          <span v-if="s.nodes.length === 0">-</span>
          <span v-else>
            {{ s.nodeNames.join(', ') }}
          </span>
        </div>
        <div
          v-if="!nameOnly"
          class="time-column"
          :class="{ modified: modifiedFields.includes('countLimit') }"
        >
          {{ listing.countLimit < 0 ? '-' : listing.countLimit }}
        </div>
        <div
          v-if="!nameOnly"
          class="time-column"
          :class="{ modified: modifiedFields.includes('orderFrom') }"
        >
          <TimeReferenceBadge
            :value="listing.orderFrom"
            :date="singleOrderSchema"
          />
        </div>
        <div
          v-if="!nameOnly"
          class="time-column"
          :class="{ modified: modifiedFields.includes('orderTo') }"
        >
          <TimeReferenceBadge
            :value="listing.orderTo"
            :date="singleOrderSchema"
          />
        </div>
        <div
          v-if="!nameOnly"
          class="time-column"
          :class="{ modified: modifiedFields.includes('cancelTo') }"
        >
          <TimeReferenceBadge
            :value="listing.cancelTo"
            :date="singleOrderSchema"
          />
        </div>
        <div
          class="time-column flex-column listing-text"
          :class="{ modified: modifiedFields.includes('subscription') }"
        >
          <template v-if="listing.subscription?.enabled">
            <div class="small font-weight-bold">
              {{ listing.subscription?.enabled ? 'TAK' : 'NIE' }}
            </div>
            <TimeReferenceBadge
              v-if="listing.subscription?.applyAt"
              :value="listing.subscription?.applyAt"
              :date="singleOrderSchema"
            />
          </template>
          <template v-else>
            <span />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import TimeReferenceBadge from '../TimeReferenceBadge';
import { formatTimetable } from './timetable';

export default {
  props: {
    checked: Boolean,
    listing: Object,
    nodes: Array,
    selected: Boolean,
    create: Boolean,
    update: Boolean,
    nameOnly: Boolean,
    hideCheckbox: Boolean,
    active: Boolean,
  },
  components: {
    TimeReferenceBadge,
  },
  computed: {
    ...mapGetters([
      'formatCurrency',
      'groups',
    ]),
    ...mapGetters('shop', [
      'products',
      'catalogDetails',
      'singleOrderSchema',
    ]),
    modifiedFields() {
      return this.listing?.modifiedFields || [];
    },
    timetableText() {
      return formatTimetable(this.listing?.calendarRules);
    },
    listingFields() {
      return this.catalogDetails.listingFields
        .slice()
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((x) => ({
          ...x,
          value: this.listing?.fields[x.fieldId] || '',
        }));
    },
    subCatalogsSorted() {
      return this.catalogDetails.subCatalogs
        .filter((x) => x.subCatalogId !== '#products')
        .slice()
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((x) => {
          const nodes = this.getSubCatalogNodes(x.subCatalogId);
          return {
            ...x,
            nodes,
            nodeNames: nodes
              .map((n) => n.name)
              .sort((a, b) => a.localeCompare(b)),
          };
        });
    },
    listingProducts() {
      if (!this.products) return [];
      return this.listing.productIds
        .map((id) => this.products.find((p) => p.id === id))
        .filter((x) => x);
    },
    listingNodes() {
      return this.listing.assignments
        .map(({ nodeId, subCatalogId }) => {
          if (subCatalogId === '#groups') {
            const group = this.groups?.find((g) => g.code === nodeId);
            if (!group) return null;
            return {
              ...group,
              subCatalogId: '#groups',
              name: group.code === '#all' ? this.$t('company.all') : group.path,
            };
          }

          return this.catalogDetails.subCatalogNodes.find((n) => n.nodeId === nodeId);
        })
        .filter((x) => x);
    },
  },
  methods: {
    getSubCatalogNodes(subCatalogId) {
      return this.listingNodes.filter((x) => x.subCatalogId === subCatalogId);
    },
  },
};
</script>

<style lang="scss" scoped>

.listing-name {
  font-size: 14px;
  min-width: 300px;
  max-width: 300px;
}

.listing-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.2;
}

.update {
  color: #888;
}

.modified {
  color: $blue;
  font-weight: 500;
  font-style: italic;
}

.photo {
  max-height: 40px;
  max-width: 40px;
  margin-right: 10px;
  mix-blend-mode: multiply;
}

.checkbox {
  background-color: #f9f9f9;
  height: 42px;
  width: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: rgba(100, 100, 100, 0.1);
  }
}

.listing-nodes {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-width: 120px;
  max-width: 120px;
  flex-basis: 120px;
  padding-right: 5px;
}

.product-badge {
  color: #fff;
  background-color: rgba($blue, 0.8);
  font-size: 11px;
  margin-bottom: 2px;
}

.update {
  .product-badge {
    background-color: rgba(#888, 0.6);
  }
  .modified {
    .product-badge {
      background-color: rgba($blue, 0.8);
    }
  }
}

.time-column {
  min-width: 100px;
  font-size: 11px;
  text-align: left;
  display: flex;
}

.row-content {
  height: 42px;
}

.money-column {
  min-width: 110px;
  font-size: 14px;
  padding-right: 15px;
}

.listing-row {
  padding: 0px 5px;
  background-color: #f9f9f9;
  min-width: min-content;
  margin: 5px 0;
  cursor: pointer;

  &:hover {
    background-color: #f3f3f3;
  }

  &.selected {
    background-color: rgba(0, 112, 255, 0.1);
  }
}

.inactive {
  opacity: 0.6;
}

.create {
  .checkbox {
    text-align: center;
    justify-content: center;
    color: $green;
    background-color: rgba($green, 0.2);
    cursor: default;
  }

  .listing-row {
    cursor: default;
    background-color: rgba($green, 0.2);
  }
}

.update {
  .checkbox {
    text-align: center;
    justify-content: center;
    color: $blue;
    background-color: rgba($blue, 0.2);
    cursor: default;
  }

  .listing-row {
    background-color: rgba($blue, 0.2);
    cursor: default;
  }
}

.node-badge {
  color: #333;
  font-size: 11px;
}
</style>
